import { useMemo, useState, useEffect, ReactNode, useCallback } from 'react';

import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { SortOrder } from 'src/services/common.types';
import { SortConfig, PageOptions } from 'src/services/api.types';
import { IContactListItem } from 'src/services/contacts/contacts.types';
import { ICompanyListItem } from 'src/services/connections/connections.types';
import { IOrgCandidateListItem } from 'src/services/candidates/candidates.types';
import { ColumnListKey } from 'src/services/settings/column-preferences.service';
import { useGetMyCompaniesLeanQuery } from 'src/services/connections/connections.service';
import { useGetOrganizationContactsLeanQuery } from 'src/services/contacts/contacts.service';
import { useGetMyCandidatesLeanQuery, useUpdateCandidateRatingMutation } from 'src/services/candidates/candidates.service';

import Iconify from 'src/components/iconify';
import DataList from 'src/components/data-list/data-list';
import RecordPanel from 'src/components/record-panel/record-panel';
import { useRecordPanel } from 'src/components/record-panel/use-record-panel';
import DataListColumnPicker from 'src/components/data-list/data-list-column-picker';
import { useColumnPreferences } from 'src/components/data-list/use-column-preferences';

import { buildContactColumns } from 'src/sections/contacts/table/contact-columns';
import { buildCandidateColumns } from 'src/sections/candidates/table/candidate-columns';
import { buildCompanyColumns } from 'src/sections/companies/table/companies/company-columns';

import ListContactRowActions from './list-contact-row-actions';
import ListCompanyRowActions from './list-company-row-actions';
import ListCandidateRowActions from './list-candidate-row-actions';

const PAGE_SIZE = 50;

/**
 * The three entity tables a saved list can render.
 *
 * DataList is generic over ONE row type but a list holds one of three, so rather than a union row
 * with casts at every render, each entity type gets its own fully-typed component here and the
 * parent switches between them. Each reuses the primary table's column registry unchanged, so a
 * contact list looks like the Contacts page and cannot drift from it.
 *
 * Rows are ENTITY rows from the primary lean endpoints scoped by `list_id` — not ListItem rows —
 * so selection carries entity ids, which is what the mailer and remove-from-list both expect.
 *
 * Each variant also mounts its OWN `RecordPanel`. It cannot be lifted to `list-detail-view`: the
 * panel needs `rows` and `count` for prev/next and the "N of M" indicator, and both are per-variant
 * state. Only one variant is mounted at a time, so the panel's default `fallback` stays correct.
 */

/** The query/sort/selection plumbing every variant repeats. */
function useListTableState(initialSort: SortConfig) {
  const [page, setPage] = useState(1);
  const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<SortConfig>(initialSort);

  const pageOptions: PageOptions = useMemo(() => ({ page, per_page: PAGE_SIZE }), [page]);
  const resetToFirstPage = useCallback(() => setPage(1), []);

  const onSortChange = useCallback((next: SortConfig) => { setSortConfig(next); resetToFirstPage(); }, [resetToFirstPage]);

  const onColumnSearchChange = useCallback((key: string, value: string) => {
    setColumnSearch((prev) => {
      const next = { ...prev };
      if (value) next[key] = value; else delete next[key];
      return next;
    });
    resetToFirstPage();
  }, [resetToFirstPage]);

  return { page, setPage, pageOptions, columnSearch, onColumnSearchChange, sortConfig, onSortChange, resetToFirstPage };
}

/** Page-1-replaces, later-pages-append, deduped by id so an overlapping refetch can't double a row. */
function useAccumulatedRows<TRow extends { id: string }>(
  currentData: { results: TRow[]; count: number } | undefined,
  page: number
) {
  const [rows, setRows] = useState<TRow[]>([]);

  useEffect(() => {
    if (!currentData) return;
    setRows((prev) => {
      if (page === 1) return currentData.results;
      const seen = new Set(prev.map((r) => r.id));
      return [...prev, ...currentData.results.filter((r) => !seen.has(r.id))];
    });
  }, [currentData, page]);

  return [rows, setRows] as const;
}

interface VariantProps {
  listId: string;
  /** Debounced global search, owned by the parent toolbar. */
  filterQuery: string;
  isTyping: boolean;
  selectedIds: string[];
  onSelectRow: (id: string) => void;
  onSelectAll: (checked: boolean, ids: string[]) => void;
  onRemoveFromList: (entityIds: string[]) => Promise<void>;
  isRemoving: boolean;
  /**
   * Builds the shared toolbar around this variant's own column picker.
   *
   * The picker is rendered HERE rather than lifted to the parent. pickerModel and save come from
   * this variant's useColumnPreferences, so reporting them upward through an effect made the parent
   * re-render the variant, which produced fresh values, which fired the effect again — an infinite
   * loop whenever those values weren't referentially stable.
   */
  renderToolbar: (columnPicker: ReactNode) => ReactNode;
}

// ----------------------------------------------------------------------

export function ListContactsTable(props: VariantProps) {
  const { listId, filterQuery, isTyping, selectedIds, onSelectRow, onSelectAll, onRemoveFromList, isRemoving, renderToolbar } = props;
  const { t } = useTranslate();
  const { openRecord } = useRecordPanel();
  const authContext = useAuthContext();
  const orgId = authContext.organization?.id;

  const state = useListTableState({ sort_by: 'last_activity_at', sort_order: SortOrder.DESC });

  const { currentData, isFetching } = useGetOrganizationContactsLeanQuery(
    {
      org_id: orgId as string,
      list_id: listId,
      search_query: filterQuery || undefined,
      column_filters: state.columnSearch,
      page: state.pageOptions,
      sort_config: state.sortConfig,
    },
    { skip: !orgId, refetchOnMountOrArgChange: true }
  );

  const [rows] = useAccumulatedRows<IContactListItem>(currentData, state.page);
  const count = currentData?.count ?? 0;

  const handleOpenOverlay = useCallback((id: string) => openRecord('contact', id, { mode: 'overlay' }), [openRecord]);
  const handleQuickView = useCallback((id: string) => openRecord('contact', id, { mode: 'quick' }), [openRecord]);

  const registry = useMemo(() => buildContactColumns({ t, onOpenRecord: handleOpenOverlay }), [t, handleOpenOverlay]);
  const { columns, pickerModel, save } = useColumnPreferences('list_contacts' as ColumnListKey, registry);

  const renderRowLeading = useCallback((row: IContactListItem) => (
    <Tooltip title={t('common.quick_view', { defaultValue: 'Quick view' })}>
      <IconButton size="small" onClick={() => handleQuickView(row.id)}>
        <Iconify icon="solar:eye-bold" />
      </IconButton>
    </Tooltip>
  ), [handleQuickView, t]);

  const renderRowActions = useCallback((row: IContactListItem) => (
    <ListContactRowActions
      row={row}
      onView={handleQuickView}
      onRemoveFromList={(r) => onRemoveFromList([r.id])}
      isRemoving={isRemoving}
    />
  ), [handleQuickView, onRemoveFromList, isRemoving]);

  return (
    <>
      <DataList<IContactListItem>
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        isSearching={isFetching || isTyping}
        hasMore={rows.length < count}
        onLoadMore={() => { if (!isFetching && rows.length < count) state.setPage((p) => p + 1); }}
        sort={state.sortConfig}
        onSortChange={state.onSortChange}
        columnSearch={state.columnSearch}
        onColumnSearchChange={state.onColumnSearchChange}
        toolbar={renderToolbar(<DataListColumnPicker model={pickerModel} onSave={save} />)}
        selectable
        selectedIds={selectedIds}
        onSelectRow={onSelectRow}
        onSelectAll={onSelectAll}
        renderRowLeading={renderRowLeading}
        renderRowActions={renderRowActions}
        emptyLabel={t('lists.detail.empty', { defaultValue: 'This list is empty' })}
      />

      <RecordPanel type="contact" ids={rows.map((r) => r.id)} total={count} />
    </>
  );
}

// ----------------------------------------------------------------------

export function ListCandidatesTable(props: VariantProps) {
  const { listId, filterQuery, isTyping, selectedIds, onSelectRow, onSelectAll, onRemoveFromList, isRemoving, renderToolbar } = props;
  const { t } = useTranslate();
  const { openRecord } = useRecordPanel();
  const tenantType = useOrgTenant();
  const authContext = useAuthContext();

  const state = useListTableState({ sort_by: 'last_activity_at', sort_order: SortOrder.DESC });

  const { currentData, isFetching } = useGetMyCandidatesLeanQuery(
    {
      list_id: listId,
      search_query: filterQuery || undefined,
      column_filters: state.columnSearch,
      page: state.pageOptions,
      sortConfig: state.sortConfig,
    },
    { skip: !authContext.organization?.id, refetchOnMountOrArgChange: true }
  );

  const [rows, setRows] = useAccumulatedRows<IOrgCandidateListItem>(currentData, state.page);
  const count = currentData?.count ?? 0;

  const [updateRating] = useUpdateCandidateRatingMutation();

  const handleOpenOverlay = useCallback((id: string) => openRecord('candidate', id, { mode: 'overlay' }), [openRecord]);
  const handleQuickView = useCallback((id: string) => openRecord('candidate', id, { mode: 'quick' }), [openRecord]);

  const handleRatingUpdate = useCallback(async (candidateId: string, rating: number) => {
    try {
      await updateRating({ candidateId, rating }).unwrap();
      // Reflect the new rating locally rather than refetching the page.
      setRows((prev) => prev.map((r) => (r.id === candidateId ? { ...r, rating } : r)));
    } catch {
      // The candidates page surfaces a toast here; a failed rating on a list is not worth one.
    }
  }, [updateRating, setRows]);

  const registry = useMemo(
    () => buildCandidateColumns({ t, tenantType, onRatingUpdate: handleRatingUpdate, onOpenRecord: handleOpenOverlay }),
    [t, tenantType, handleRatingUpdate, handleOpenOverlay]
  );
  const { columns, pickerModel, save } = useColumnPreferences('list_candidates' as ColumnListKey, registry);

  const renderRowLeading = useCallback((row: IOrgCandidateListItem) => (
    <Tooltip title={t('common.quick_view', { defaultValue: 'Quick view' })}>
      <IconButton size="small" onClick={() => handleQuickView(row.id)}>
        <Iconify icon="solar:eye-bold" />
      </IconButton>
    </Tooltip>
  ), [handleQuickView, t]);

  const renderRowActions = useCallback((row: IOrgCandidateListItem) => (
    <ListCandidateRowActions
      row={row}
      onView={handleQuickView}
      onRemoveFromList={(r) => onRemoveFromList([r.id])}
      isRemoving={isRemoving}
    />
  ), [handleQuickView, onRemoveFromList, isRemoving]);

  return (
    <>
      <DataList<IOrgCandidateListItem>
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        isSearching={isFetching || isTyping}
        hasMore={rows.length < count}
        onLoadMore={() => { if (!isFetching && rows.length < count) state.setPage((p) => p + 1); }}
        sort={state.sortConfig}
        onSortChange={state.onSortChange}
        columnSearch={state.columnSearch}
        onColumnSearchChange={state.onColumnSearchChange}
        toolbar={renderToolbar(<DataListColumnPicker model={pickerModel} onSave={save} />)}
        selectable
        selectedIds={selectedIds}
        onSelectRow={onSelectRow}
        onSelectAll={onSelectAll}
        renderRowLeading={renderRowLeading}
        renderRowActions={renderRowActions}
        emptyLabel={t('lists.detail.empty', { defaultValue: 'This list is empty' })}
      />

      <RecordPanel type="candidate" ids={rows.map((r) => r.id)} total={count} />
    </>
  );
}

// ----------------------------------------------------------------------

export function ListCompaniesTable(props: VariantProps) {
  const { listId, filterQuery, isTyping, selectedIds, onSelectRow, onSelectAll, onRemoveFromList, isRemoving, renderToolbar } = props;
  const { t } = useTranslate();
  const { openRecord } = useRecordPanel();

  const state = useListTableState({ sort_by: 'company_name', sort_order: SortOrder.ASC });

  const { currentData, isFetching } = useGetMyCompaniesLeanQuery(
    {
      params: {
        list_id: listId,
        search_query: filterQuery || undefined,
        column_filters: state.columnSearch,
        ...state.pageOptions,
        ...state.sortConfig,
      },
    },
    { refetchOnMountOrArgChange: true }
  );

  const [rows] = useAccumulatedRows<ICompanyListItem>(currentData, state.page);
  const count = currentData?.count ?? 0;

  const handleOpenOverlay = useCallback((id: string) => openRecord('company', id, { mode: 'overlay' }), [openRecord]);
  const handleQuickView = useCallback((id: string) => openRecord('company', id, { mode: 'quick' }), [openRecord]);

  const registry = useMemo(() => buildCompanyColumns({ t, onOpenRecord: handleOpenOverlay }), [t, handleOpenOverlay]);
  const { columns, pickerModel, save } = useColumnPreferences('list_companies' as ColumnListKey, registry);

  const renderRowLeading = useCallback((row: ICompanyListItem) => (
    <Tooltip title={t('common.quick_view', { defaultValue: 'Quick view' })}>
      <IconButton size="small" onClick={() => handleQuickView(row.id)}>
        <Iconify icon="solar:eye-bold" />
      </IconButton>
    </Tooltip>
  ), [handleQuickView, t]);

  const renderRowActions = useCallback((row: ICompanyListItem) => (
    <ListCompanyRowActions
      row={row}
      onView={handleQuickView}
      onRemoveFromList={(r) => onRemoveFromList([r.id])}
      isRemoving={isRemoving}
    />
  ), [handleQuickView, onRemoveFromList, isRemoving]);

  return (
    <>
      <DataList<ICompanyListItem>
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        isSearching={isFetching || isTyping}
        hasMore={rows.length < count}
        onLoadMore={() => { if (!isFetching && rows.length < count) state.setPage((p) => p + 1); }}
        sort={state.sortConfig}
        onSortChange={state.onSortChange}
        columnSearch={state.columnSearch}
        onColumnSearchChange={state.onColumnSearchChange}
        toolbar={renderToolbar(<DataListColumnPicker model={pickerModel} onSave={save} />)}
        selectable
        selectedIds={selectedIds}
        onSelectRow={onSelectRow}
        onSelectAll={onSelectAll}
        renderRowLeading={renderRowLeading}
        renderRowActions={renderRowActions}
        emptyLabel={t('lists.detail.empty', { defaultValue: 'This list is empty' })}
      />

      <RecordPanel type="company" ids={rows.map((r) => r.id)} total={count} />
    </>
  );
}
